/**
 * RoleListPage.jsx
 * Route: /roles-and-permissions
 * Navigates to RoleFormPage for create/edit.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit, Trash2, Shield, ChevronRight, Star, Search, Filter, X } from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import { TfiAnchor } from "react-icons/tfi";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import PageLoader from "../../helpers/PageLoader";
import NoDataAvailable from "../../helpers/NoDataAvailable";
import DeleteConfirmationOffcanvas from "../../helpers/DeleteConfirmationOffcanvas";
import SelectDropdown from "../../customStyles/SelectDropdown"; // Unkaloda SelectDropdown path
import { ALL_PERMISSION_KEYS, PERMISSION_MODULES } from "../../config/permissionModules";

// ── helpers ──────────────────────────────────────────────────────────────────
const BADGE_COLORS = [
  "bg-pink-400", "bg-red-400", "bg-green-500",
  "bg-yellow-500", "bg-blue-500", "bg-purple-500",
  "bg-indigo-500", "bg-teal-500",
];
const badgeColor = (i) => BADGE_COLORS[i % BADGE_COLORS.length];

const fullName = (u) =>
  u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() || "Unknown" : "Unknown";

/** Count how many permission keys have at least one action allowed */
const countGrantedKeys = (permissions = {}) =>
  ALL_PERMISSION_KEYS.filter((k) => {
    const p = permissions[k];
    return p && (p.view || p.add || p.edit || p.delete || p.export);
  }).length;

// ─────────────────────────────────────────────────────────────────────────────

const RoleListPage = () => {
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteState, setDeleteState] = useState({ isOpen: false, role: null, error: null });

  // ── Filter States ──────────────────────────────────────────────────────────
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [defaultFilter, setDefaultFilter] = useState(""); // 'default' or 'custom'
  const [moduleFilter, setModuleFilter] = useState(""); // specific module key e.g. 'sales'
  const [sortBy, setSortBy] = useState("name-asc"); // 'name-asc', 'name-desc', 'date-desc'

  // ── fetch ────────────────────────────────────────────────────────────────  
  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(SummaryApi.getAllRoles.url);
      setRoles(res.data.data || []);
    } catch (err) {
      console.error("fetchRoles:", err);
      toast.error("Failed to load roles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  // ── actions ────────────────────────────────────────────────────────────────
  const handleMarkDefault = async (roleId) => {
    try {
      await api.put(SummaryApi.updateRole.url(roleId), { isDefault: true });
      toast.success("Marked as default");
      setRoles((prev) =>
        prev.map((r) =>
          r._id === roleId ? { ...r, isDefault: true } : { ...r, isDefault: false }
        )
      );
    } catch (err) {
      toast.error("Failed to update default");
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      const res = await api.delete(SummaryApi.deleteRole.url(roleId));
      if (res.data?.success === false) {
        setDeleteState((prev) => ({
          ...prev,
          error: { message: res.data.message, users: res.data.data },
        }));
      } else {
        toast.success("Role deleted");
        setRoles((prev) => prev.filter((r) => r._id !== roleId));
        setDeleteState({ isOpen: false, role: null, error: null });
      }
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.data) {
        setDeleteState((prev) => ({
          ...prev,
          error: { message: errData.message, users: errData.data },
        }));
      } else {
        toast.error(errData?.message || "Error deleting role");
        setDeleteState({ isOpen: false, role: null, error: null });
      }
    }
  };

  // ── Filtered & Sorted Roles Logic ──────────────────────────────────────────
  const filteredAndSortedRoles = useMemo(() => {
    return roles
      .filter((role) => {
        // Search term (roleName or description)
        if (searchTerm) {
          const s = searchTerm.toLowerCase();
          const nameMatch = role.roleName?.toLowerCase().includes(s);
          const descMatch = role.description?.toLowerCase().includes(s);
          if (!nameMatch && !descMatch) return false;
        }

        // Default Filter
        if (defaultFilter === "default" && !role.isDefault) return false;
        if (defaultFilter === "custom" && role.isDefault) return false;

        // Module Filter (Role must have 'view' permission enabled for at least one key matching the module)
        if (moduleFilter) {
          const perms = role.permissions || {};
          const hasModuleAccess = Object.keys(perms).some((key) => {
            return key.startsWith(moduleFilter) && perms[key]?.view === true;
          });
          if (!hasModuleAccess) return false;
        }

        return true;
      })
      .sort((a, b) => {
        // Default role always on top if default sort behavior preferred, or use dynamic sort
        if (sortBy === "name-asc") {
          return (a.roleName || "").localeCompare(b.roleName || "");
        }
        if (sortBy === "name-desc") {
          return (b.roleName || "").localeCompare(a.roleName || "");
        }
        if (sortBy === "date-desc") {
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        }
        return 0;
      });
  }, [roles, searchTerm, defaultFilter, moduleFilter, sortBy]);

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="rounded-lg bg-red-50 border border-red-100 hover:border-primary text-textPrimary hover:text-primary p-2 transition-colors"
          >
            <FaArrowLeft className="w-4 h-4" />
          </button>
          <Shield className="w-6 h-6 text-primary" />
          <h1 className="text-xl font-bold text-gray-800">Roles & Permissions</h1>
        </div>

        <button
          onClick={() => navigate("create")}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Role
        </button>
      </div>

      {/* Info banner */}
      <div className="mb-5 rounded-xl bg-purple-50 border border-purple-100 px-4 py-3 text-sm text-purple-800">
        <strong>Role-based access:</strong> Each user inherits permissions from their assigned role.
        The <strong>Owner</strong> account always has full access regardless of role.
        Mark a role as <strong>Default</strong> to auto-assign it to new users.
      </div>

      {/* Search and Filters Section */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search roles by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary outline-none bg-white text-sm"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-xl transition-colors text-sm font-medium ${
              showFilters ? "bg-purple-50 border-purple-300 text-purple-700" : "bg-white border-gray-300 text-gray-700"
            }`}
          >
            <Filter className="h-4 w-4" /> Filters
          </button>
        </div>

        {/* Collapsible Filter Dropdowns */}
        {showFilters && (
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-in fade-in slide-in-from-top-2">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Role Type</label>
              <SelectDropdown
                options={[
                  { id: "", label: "All Types" },
                  { id: "default", label: "Default Role" },
                  { id: "custom", label: "Custom Roles" },
                ]}
                value={defaultFilter}
                onChange={setDefaultFilter}
                valueKey="id"
                labelKey="label"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Module Access</label>
              <SelectDropdown
                options={[
                  { key: "", label: "All Modules" },
                  ...PERMISSION_MODULES.map((m) => ({ key: m.key, label: m.label }))
                ]}
                value={moduleFilter}
                onChange={setModuleFilter}
                valueKey="key"
                labelKey="label"
                searchable={true}
                placeholder="Filter by Module"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Sort By</label>
              <SelectDropdown
                options={[
                  { id: "name-asc", label: "Role Name (A - Z)" },
                  { id: "name-desc", label: "Role Name (Z - A)" },
                  { id: "date-desc", label: "Date Created (Newest)" },
                ]}
                value={sortBy}
                onChange={setSortBy}
                valueKey="id"
                labelKey="label"
              />
            </div>

            <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
              <button
                onClick={() => {
                  setSearchTerm("");
                  setDefaultFilter("");
                  setModuleFilter("");
                  setSortBy("name-asc");
                }}
                className="text-red-600 text-xs font-medium flex items-center gap-1 hover:underline"
              >
                <X className="h-3.5 w-3.5" /> Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <PageLoader />
      ) : filteredAndSortedRoles.length === 0 ? (
        <NoDataAvailable />
      ) : (
        <div className="space-y-2">
          {filteredAndSortedRoles.map((role, idx) => {
            const granted = countGrantedKeys(role.permissions);
            const total = ALL_PERMISSION_KEYS.length;

            return (
              <div
                key={role._id}
                className={`group flex items-center justify-between rounded-xl border px-4 py-4 transition-all duration-200 hover:shadow-md ${
                  role.isDefault ? "bg-purple-50 border-purple-200" : "bg-white border-gray-200"
                }`}
              >
                {/* Left: avatar + info */}
                <div className="flex items-center gap-4">
                  <div
                    className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${badgeColor(idx)}`}
                  >
                    {(role.roleName || "?").slice(0, 2).toUpperCase()}
                  </div>

                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800">{role.roleName}</span>
                      {role.isDefault && (
                        <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                          <Star className="w-3 h-3" /> Default
                        </span>
                      )}
                    </div>

                    {role.description && (
                      <p className="text-xs text-gray-500 mt-0.5 max-w-xs truncate">
                        {role.description}
                      </p>
                    )}

                    <div className="flex items-center gap-3 mt-1">
                      {/* Permission bar */}
                      <div className="flex items-center gap-1.5">
                        <div className="w-24 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-purple-500 transition-all"
                            style={{ width: `${Math.round((granted / total) * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">
                          {granted}/{total} modules
                        </span>
                      </div>

                      <span className="text-xs text-gray-400">
                        Created by{" "}
                        <span className="text-gray-600 font-medium">
                          {fullName(role.createdBy)}
                        </span>
                        {role.createdAt && (
                          <> · {format(new Date(role.createdAt), "dd MMM yyyy")}</>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right: actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {/* Mark as default */}
                  <button
                    onClick={() => !role.isDefault && handleMarkDefault(role._id)}
                    title={role.isDefault ? "Already default" : "Mark as default"}
                    disabled={role.isDefault}
                    className={`p-2 rounded-lg transition-colors ${
                      role.isDefault
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:text-purple-600 hover:bg-purple-50"
                    }`}
                  >
                    <TfiAnchor className="w-4 h-4" />
                  </button>

                  {/* Edit → navigate to form */}
                  <button
                    onClick={() => navigate(`${role._id}/edit`)}
                    title="Edit role"
                    className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>

                  {/* Delete */}
                  <button
                    onClick={() =>
                      !role.isDefault &&
                      setDeleteState({ isOpen: true, role, error: null })
                    }
                    title={role.isDefault ? "Cannot delete default role" : "Delete role"}
                    disabled={role.isDefault}
                    className={`p-2 rounded-lg transition-colors ${
                      role.isDefault
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:text-primary hover:bg-red-50"
                    }`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  {/* View permissions quick-link */}
                  <button
                    onClick={() => navigate(`${role._id}/edit`)}
                    title="View permissions"
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete confirmation */}
      <DeleteConfirmationOffcanvas
        isOpen={deleteState.isOpen}
        onClose={() => setDeleteState({ isOpen: false, role: null, error: null })}
        onConfirm={() => handleDeleteRole(deleteState.role?._id)}
        title="Delete Role"
        itemName={deleteState.role?.roleName}
        error={deleteState.error}
      />
    </div>
  );
};

export default RoleListPage;